import { useState, useEffect, useCallback } from 'react';
import { Contact, FunnelStage } from '../../../types';
import { maskCurrency, parseCurrency } from '../../../hooks/useCurrencyInput';

interface OpportunityFormData {
    stage: string;
    contactId: string;
    source: string;
    assignedTo: string;
    title: string;
    value: string;
}

interface UseOpportunityFormProps {
    isOpen: boolean;
    funnelStages: FunnelStage[];
    /**
     * Deve retornar `true` em sucesso (form fecha + sessionStorage limpo)
     * ou `false` em falha (form permanece aberto preservando os dados).
     */
    onSave: (data: any) => Promise<boolean> | boolean;
    onClose: () => void;
    /** Contato recém-criado no modal aninhado — auto-seleciona no formulário */
    createdContact?: Contact | null;
    onCreatedContactApplied?: () => void;
}

export function useOpportunityForm({
    isOpen,
    funnelStages,
    onSave,
    onClose,
    createdContact,
    onCreatedContactApplied
}: UseOpportunityFormProps) {
    const [formData, setFormData] = useState<OpportunityFormData>(() => {
        if (typeof window !== 'undefined') {
            const saved = sessionStorage.getItem('kanban_form_data');
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch (e) { }
            }
        }
        return {
            stage: '',
            contactId: '',
            source: '',
            assignedTo: '',
            title: '',
            value: '0,00'
        };
    });

    // Reset form when opening
    useEffect(() => {
        if (isOpen) {
            const saved = sessionStorage.getItem('kanban_form_data');
            if (saved) {
                try {
                    setFormData(JSON.parse(saved));
                    return;
                } catch (e) {}
            }
            
            // Clean state if there is no session restoring
            setFormData({
                stage: funnelStages.length > 0 ? funnelStages[0].id : '',
                contactId: '',
                source: '',
                assignedTo: '',
                title: '',
                value: '0,00'
            });
        }
    }, [isOpen, funnelStages]);

    const handleSelectContact = useCallback((contact: Contact) => {
        setFormData(prev => {
            const next = {
                ...prev,
                contactId: contact.id,
                title: `Negociação - ${contact.name}`
            };
            sessionStorage.setItem('kanban_form_data', JSON.stringify(next));
            return next;
        });
    }, []);

    // Auto-seleciona contato recém-persistido antes de permitir criar a negociação
    useEffect(() => {
        if (!isOpen || !createdContact?.id) return;
        handleSelectContact(createdContact);
        onCreatedContactApplied?.();
    }, [isOpen, createdContact, handleSelectContact, onCreatedContactApplied]);

    const handleClearContact = useCallback(() => {
        setFormData(prev => {
            const next = {
                ...prev,
                contactId: '',
                title: ''
            };
            sessionStorage.setItem('kanban_form_data', JSON.stringify(next));
            return next;
        });
    }, []);

    const handleValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskCurrency(e.target.value);
        setFormData(prev => {
            const next = { ...prev, value: masked };
            sessionStorage.setItem('kanban_form_data', JSON.stringify(next));
            return next;
        });
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string, value: string } }) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            sessionStorage.setItem('kanban_form_data', JSON.stringify(next));
            return next;
        });
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!formData.contactId?.trim()) {
            return;
        }

        const valueFloat = parseCurrency(formData.value);

        try {
            setIsSubmitting(true);
            const ok = await onSave({
                stage: formData.stage,
                contactId: formData.contactId,
                source: formData.source,
                assignedTo: formData.assignedTo,
                title: formData.title,
                value: valueFloat
            });

            if (ok) {
                sessionStorage.removeItem('kanban_form_data');
                onClose();
            }
            // Em falha: mantem sidebar aberta, dados preservados em sessionStorage
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, onSave, onClose, isSubmitting]);

    return {
        formData,
        handleSelectContact,
        handleClearContact,
        handleValueChange,
        handleChange,
        handleSubmit,
        isSubmitting
    };
}
